import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";
import { delay } from "../../helper/delayresponse";

interface SmtpCampaign {
  id?: number;
  name: string;
  emails: File[] | null;
  subject: string;
  smtp_gateway_id: number;
  status?: number;
  message: string;
  retries: number;
  retry_minutes: number;
  schedule_time: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface SmtpCampaignState {
  smtpcampaigndata: SmtpCampaign[];
  info: {
    count: number;
    current_page: number;
    limit: number;
    total_page: number;
  } | null;
  error: string | null;
  loading: boolean;
}

// ====================-SmtpCampgain-listing-action-start==============
export const GetallSmtpCampaign = createAsyncThunk<
  {
    data: SmtpCampaign[];
    info: {
      count: number;
      current_page: number;
      limit: number;
      total_page: number;
    };
  },
  { search: string; page: number; limit: number },
  { rejectValue: string }
>(
  "GetallSmtpCampaign",
  async ({ search, page, limit }, { rejectWithValue }) => {
    try {
      await delay(1000);
      const response = await axiosInstance.get(`api/admin/smtp-campaign`, {
        params: { search, page, limit },
      });
      return { data: response.data.data, info: response.data.info };
    } catch (error: any) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);
// ====================SmtpCampgain-listing-action-start==============

const formatDateTimeTo12Hour = (datetimeValue: string) => {
  if (!datetimeValue) return null
  const [date, time] = datetimeValue.split("T");
  let [hours, minutes] = time.split(":");

  const isPM = Number(hours) >= 12;
  const ampm = isPM ? "PM" : "AM";
  hours = String(Number(hours) % 12 || 12).padStart(2, "0");

  return `${date} ${hours}:${minutes}:00 ${ampm}`;
};


// ==================AddSmtpCampaigns-action-start===========
export const AddSmtpCampaigns = createAsyncThunk<
  SmtpCampaign,
  { addData: any }
>("smtpcampaigndata/AddSmtpCampaigns", async ({ addData }) => {
  try {
    const formData = new FormData();
    formData.append("name", addData.name);
    formData.append("smtp_gateway_id", addData?.smtpgatewayid?.toString());
    formData.append("subject", addData.subject);
    formData.append("message", addData.message);
    formData.append("schedule_time", formatDateTimeTo12Hour(addData?.scheduletime || "")!);
    formData.append("retries", addData.retries.toString());
    formData.append("retry_minutes", addData.retry_minutes.toString());

    formData.append("cc", addData?.cc.toString());
    formData.append("bcc", addData?.bcc.toString());
    formData.append("reply_to", addData?.reply_to.toString());
    formData.append("send_report_emails", addData?.send_report_emails.toString());
    formData.append("attachments", JSON.stringify(addData.attachments));

    if (addData.emails)
      addData.emails.forEach((file: any) => {
        formData.append("emails", file);
      });

    const { data } = await axiosInstance.post(
      "/api/admin/smtp-campaign/create",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  } catch (error: any) {
    console.log(error);
    if (error.response && error.response.data) {
      return error.response.data || "An error occurred";
    }
    return error.message;
  }
});
// ==================AddSmtpCampaigns-action-end===========

// =================UpdateSmtp-Campaigns-action-start=========
export const UpdateSmtpCampaigns = createAsyncThunk<
  SmtpCampaign,
  { id: number; updatedData: any },
  { rejectValue: string }
>(
  "smtpcampaigndata/UpdateSmtpCampaigns",
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("name", updatedData.name);
      formData.append(
        "smtp_gateway_id",
        updatedData?.smtpgatewayid.toString()
      );
      formData.append("subject", updatedData.subject);
      formData.append("message", updatedData.message);
      formData.append("schedule_time", formatDateTimeTo12Hour(updatedData?.scheduletime || "")!);
      formData.append("retries", updatedData.retries.toString());
      formData.append("retry_minutes", updatedData.retry_minutes.toString());

      formData.append("cc", updatedData?.cc.toString());
      formData.append("bcc", updatedData?.bcc.toString());
      formData.append("reply_to", updatedData?.reply_to.toString());
      formData.append("send_report_emails", updatedData?.send_report_emails.toString());
      formData.append("attachments", JSON.stringify(updatedData.attachments));

      if (updatedData.emails)
        updatedData.emails.forEach((file: any) => {
          formData.append("emails", file);
        });

      const response = await axiosInstance.put(
        `api/admin/smtp-campaign/update/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error("Error during API call:", error);
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);

// =================UpdateSmtp-Campaigns-action-end=========

// =================Update-smtpcampaign-status-action-start=============
export const UpdateSmtpCampaignsStatus = createAsyncThunk<
  SmtpCampaign,
  { id: number; status: number },
  { rejectValue: string }
>(
  "smtpcampaigndata/UpdateSmtpCampaignsStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `api/admin/smtp-campaign/update/status/${id}`,
        { status }
      );
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);
// =================Update-smtpcampaign-status-action-end=============

// ================DeleteSmtpCampaign-action-start=========
export const DeleteSmtpCampaign = createAsyncThunk<
  { id: number },
  { id: number }
>("smtpcampaigndata/DeleteSmtpCampaign", async ({ id }) => {
  try {
    const { data } = await axiosInstance.delete(
      `api/admin/smtp-campaign/${id}`
    );
    return data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      return error.response.data;
    }
    return error.message;
  }
});
// ================DeleteSmtpCampaign-action-end=========

// ===============sms-campaign-media-action-start=================
export const AddSmtpCampaignsMedia = createAsyncThunk<
  SmtpCampaign,
  { formData: any }
>("smtpcampaigndata/AddSmtpCampaignsMedia", async ({ formData }) => {
  try {

    const { data } = await axiosInstance.post(
      "api/admin/smtp-campaign/media",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  } catch (error: any) {
    console.log(error);
    if (error.response && error.response.data) {
      return error.response.data || "An error occurred";
    }
    return error.message;
  }
});
// ===============sms-campaign-media-action-end=================


const initialState: SmtpCampaignState = {
  smtpcampaigndata: [],
  info: null,
  error: null,
  loading: false,
};

const smtpcampaignSlice = createSlice({
  name: "Smtpcampaign",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ===============get-all-smtp-slice-start============
      .addCase(GetallSmtpCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        GetallSmtpCampaign.fulfilled,
        (
          state,
          action: PayloadAction<{
            data: SmtpCampaign[];
            info: {
              count: number;
              current_page: number;
              limit: number;
              total_page: number;
            };
          }>
        ) => {
          state.loading = false;
          state.smtpcampaigndata = action.payload.data;
          state.info = action.payload.info;
        }
      )
      .addCase(
        GetallSmtpCampaign.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "An error occurred";
        }
      );
    // ===============get-all-smtp-slice-end============

    // =================add-=smtp-slice-start============
    builder
      .addCase(AddSmtpCampaigns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        AddSmtpCampaigns.fulfilled,
        (state, action: PayloadAction<SmtpCampaign>) => {
          state.loading = false;
          state.smtpcampaigndata.push(action.payload);
        }
      )
      .addCase(AddSmtpCampaigns.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload || "Failed to add smtpcampaign";
      });
    // =================add-=smtp-slice-end============

    // =================Update-smtpcampaign-status-slice-start============
    builder
      .addCase(UpdateSmtpCampaignsStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        UpdateSmtpCampaignsStatus.fulfilled,
        (state, action: PayloadAction<SmtpCampaign>) => {
          state.loading = false;
          const index = state.smtpcampaigndata.findIndex(
            (item) => item.id === action.payload.id
          );
          if (index !== -1) {
            state.smtpcampaigndata[index] = action.payload;
          }
        }
      )
      .addCase(
        UpdateSmtpCampaignsStatus.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to update smtpcampaignstatus";
        }
      );
    // =================Update-smtpcampaign-status-slice-end============

    // =================Update-smtp-slice-start============
    builder
      .addCase(UpdateSmtpCampaigns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        UpdateSmtpCampaigns.fulfilled,
        (state, action: PayloadAction<SmtpCampaign>) => {
          state.loading = false;
          const index = state.smtpcampaigndata.findIndex(
            (item) => item.id === action.payload.id
          );
          if (index !== -1) {
            state.smtpcampaigndata[index] = action.payload;
          }
        }
      )
      .addCase(
        UpdateSmtpCampaigns.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to update smtpcampaign";
        }
      );
    // =================Update-smtp-slice-end============

    // =================Delete-smtp-slice-start============
    builder
      .addCase(DeleteSmtpCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        DeleteSmtpCampaign.fulfilled,
        (state, action: PayloadAction<{ id: number }>) => {
          state.loading = false;
        }
      )
      .addCase(DeleteSmtpCampaign.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete smtpcampaign";
      });
    // =================Delete-smtp-slice-end============


    // ==================Add-Smtp-campains-Media-slice-start============
    builder
      .addCase(AddSmtpCampaignsMedia.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        AddSmtpCampaignsMedia.fulfilled,
        (state, action: PayloadAction<SmtpCampaign>) => {
          state.loading = false;
          state.smtpcampaigndata.push(action.payload);
        }
      )
      .addCase(AddSmtpCampaignsMedia.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload || "Failed to add smtpcampaign";
      });
    // ==================AddSmtp-campains-Media-slice-end============
  },
});

export default smtpcampaignSlice.reducer;
